import React, { useMemo } from 'react';
import { pick } from '../../../utils/operators/pick';
import { Text } from '../../../Text';
import { classes, st } from '../../Tabs.st.css';
import { TabItemProps } from './TabItem.types';

const TabItem = ({
  item,
  onItemClick,
  isActive,
  dynamicProperties,
  size,
  type,
  alignment,
  width,
  itemMaxWidth,
  scrollOnOverflow,
  ...restProps
}: TabItemProps) => {
  const { minWidth, maxWidth, disabled } = item;

  const textSize = size === 'smallMedium' ? 'small' : size;

  const itemStyle = useMemo<React.CSSProperties>(() => {
    let inlineStyle: React.CSSProperties = {};

    if (type === 'uniformSide' && width) {
      inlineStyle = { width };
    } else if (type === 'compactSide' && itemMaxWidth) {
      inlineStyle = { maxWidth: `${itemMaxWidth}pt` };
    }

    if (minWidth) {
      inlineStyle.minWidth = minWidth;
    }

    if (maxWidth) {
      inlineStyle.maxWidth = maxWidth;
    }

    return inlineStyle;
  }, [type, width, itemMaxWidth, minWidth, maxWidth]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ' || e.key === 'Space' || e.key === 'Enter') {
      e.preventDefault();
      onItemClick?.(item);
    }
  };

  return (
    <button
      role="tab"
      type="button"
      aria-selected={isActive}
      aria-disabled={disabled}
      data-active={isActive}
      data-hook={item.dataHook}
      key={item.id}
      onClick={!disabled && onItemClick ? () => onItemClick(item) : undefined}
      style={itemStyle}
      {...pick(restProps, dynamicProperties)}
      className={st(classes.tab, {
        size,
        active: isActive,
        type,
        alignment,
        disabled,
      })}
      onKeyDown={!disabled ? handleKeyDown : undefined}
    >
      {item.prefix && <div className={st(classes.affix)}>{item.prefix}</div>}

      <Text
        size={textSize}
        className={classes.tabText}
        weight="thin"
        ellipsis={!scrollOnOverflow}
      >
        {item.title}
      </Text>

      {item.suffix && <div className={st(classes.affix)}>{item.suffix}</div>}
    </button>
  );
};

export { TabItem };
